import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import {
  animate,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type MotionValue
} from 'framer-motion';
import { subscribe, getSnapshot } from '@/services/navProgress';

/** Минимальная видимость после показа, чтобы добег читался даже на быстром переходе. */
const MIN_VISIBLE_MS = 400;
/** Предохранитель: дольше этого полоса висеть не может (защита от «залипания»). */
const MAX_VISIBLE_MS = 10000;
/** Стартовое заполнение при показе. */
const START_SCALE = 0.08;
/** Асимптота «ползущего» прогресса, пока идёт переход. */
const TRICKLE_TARGET = 0.9;
/** Длительность медленного подъёма к TRICKLE_TARGET (сек). */
const TRICKLE_DURATION = 8;
/** Длительность добега к 100% на финише (сек). */
const FINISH_DURATION = 0.2;
/** Длительность растворения после добега (сек). */
const FADE_DURATION = 0.25;

type TimerRef = MutableRefObject<ReturnType<typeof setTimeout> | null>;

interface ITopLoadingBarState {
  /** Заполнение полосы (scaleX 0..1). */
  progress: MotionValue<number>;
  /** Обратный масштаб «кометы» — компенсирует scaleX родителя, убирая искажение. */
  inverseProgress: MotionValue<number>;
  /** Прозрачность контейнера. */
  opacity: MotionValue<number>;
  /** Смонтирована ли внутренность полосы (гейт для цикла «кометы»). */
  visible: boolean;
  /** Пользователь просит уменьшить анимации. */
  reduceMotion: boolean;
}

/**
 * Контроллер анимации глобальной полосы загрузки.
 *
 * Подписан на стор перехода НАПРЯМУЮ (не через useSyncExternalStore), чтобы
 * ловить каждый флип признака загрузки — включая мгновенный true→false тёплого
 * (закешированного) перехода, который React иначе схлопнул бы в одно значение.
 * Полоса показывается сразу при старте перехода и держится минимум
 * MIN_VISIBLE_MS, поэтому обратная связь видна на каждом переходе, а на медленном
 * соединении полоса «ползёт» вплоть до готовности роута.
 */
export const useTopLoadingBar = (): ITopLoadingBarState => {
  const reduceMotion = useReducedMotion() ?? false;

  const progress = useMotionValue(0);
  const opacity = useMotionValue(0);
  const inverseProgress = useTransform(progress, (value) =>
    value > 0.02 ? 1 / value : 50
  );

  const [visible, setVisible] = useState(false);

  const shownAtRef = useRef<number | null>(null);
  const finishTimerRef: TimerRef = useRef(null);
  const maxTimerRef: TimerRef = useRef(null);
  // Поколение цикла показа: устаревший финиш не должен сбросить новый цикл.
  const genRef = useRef(0);

  useEffect(() => {
    const clearTimer = (ref: TimerRef): void => {
      if (ref.current !== null) {
        clearTimeout(ref.current);
        ref.current = null;
      }
    };

    const finish = (gen: number): void => {
      if (gen !== genRef.current) {
        return;
      }
      clearTimer(maxTimerRef);
      animate(progress, 1, {
        duration: reduceMotion ? 0.12 : FINISH_DURATION,
        ease: [0.25, 0.8, 0.35, 1],
        onComplete: () => {
          if (gen !== genRef.current) {
            return;
          }
          animate(opacity, 0, {
            duration: reduceMotion ? 0.1 : FADE_DURATION,
            onComplete: () => {
              if (gen !== genRef.current) {
                return;
              }
              setVisible(false);
              shownAtRef.current = null;
              progress.set(0);
            }
          });
        }
      });
    };

    const startCycle = (): void => {
      const gen = ++genRef.current;
      shownAtRef.current = Date.now();
      setVisible(true);
      progress.set(START_SCALE);
      animate(opacity, 1, { duration: reduceMotion ? 0.01 : 0.12 });
      animate(progress, TRICKLE_TARGET, {
        duration: reduceMotion ? 0.4 : TRICKLE_DURATION,
        ease: [0.1, 0.7, 0.25, 1]
      });
      clearTimer(maxTimerRef);
      maxTimerRef.current = setTimeout(() => {
        finish(gen);
      }, MAX_VISIBLE_MS);
    };

    // Старт перехода: показываем полосу; если уже показываемся — продолжаем цикл,
    // отменив отложенный финиш (новый переход «перебивает» завершение прошлого).
    const handleStart = (): void => {
      clearTimer(finishTimerRef);
      if (shownAtRef.current === null) {
        startCycle();
      }
    };

    // Роут смонтирован: планируем финиш, гарантируя минимальную видимость.
    const handleSettle = (): void => {
      if (shownAtRef.current === null) {
        return;
      }
      const elapsed = Date.now() - shownAtRef.current;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      const gen = genRef.current;
      clearTimer(finishTimerRef);
      finishTimerRef.current = setTimeout(() => {
        finishTimerRef.current = null;
        finish(gen);
      }, wait);
    };

    const unsubscribe = subscribe(() => {
      if (getSnapshot()) {
        handleStart();
      } else {
        handleSettle();
      }
    });

    return () => {
      unsubscribe();
      clearTimer(finishTimerRef);
      clearTimer(maxTimerRef);
    };
  }, [progress, opacity, reduceMotion]);

  return { progress, inverseProgress, opacity, visible, reduceMotion };
};
